import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { appColors } from '../Config/Theme';
import { getHelpTopicBySlug } from '../help/helpSearch';
import AppBreadcrumbs from '../Components/Navigation/AppBreadcrumbs';
import HelpRoleChips from '../Components/Help/HelpRoleChips';
import HelpRelatedTopics from '../Components/Help/HelpRelatedTopics';
import HelpOnThisPageNav from '../Components/Help/HelpOnThisPageNav';

const HelpTopicPage: React.FC = () => {
  const { topicSlug } = useParams<{ topicSlug: string }>();
  const navigate = useNavigate();
  const topic = topicSlug ? getHelpTopicBySlug(topicSlug) : undefined;

  if (!topic) {
    return (
      <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: 3, maxWidth: 1200, mx: 'auto', width: '100%' }}>
        <AppBreadcrumbs items={[{ label: 'Help', path: '/help' }, { label: 'Not Found' }]} />
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1 }}>
            Help Topic Not Found
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mb: 3 }}>
            The requested help topic does not exist.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/help')} startIcon={<BackIcon />}>
            Back to Help Center
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      <AppBreadcrumbs items={[{ label: 'Help', path: '/help' }, { label: topic.title }]} />

      <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1 }}>
            {topic.title}
          </Typography>
          <Typography variant="body1" sx={{ color: appColors.text.body, mb: 2, lineHeight: 1.6 }}>
            {topic.summary}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <HelpRoleChips audience={topic.audience} />
          </Box>

          {topic.relatedRoutes && topic.relatedRoutes.length > 0 && (
            <Box
              sx={{
                mb: 3,
                p: 1.5,
                borderRadius: '8px',
                bgcolor: appColors.surface.inactive,
                border: `1px solid ${appColors.border.default}`,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: appColors.text.caption, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Where to find it
              </Typography>
              <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.5, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {topic.relatedRoutes.join(', ')}
              </Typography>
            </Box>
          )}

          {/* Sections */}
          {topic.sections.map((section) => (
            <Box key={section.id} id={section.id} sx={{ mb: 4, scrollMarginTop: '80px' }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: appColors.text.heading, mb: 1.5, fontSize: '1.05rem' }}>
                {section.title}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  color: appColors.text.body,
                  lineHeight: 1.7,
                  '& ul, & ol': { pl: 2.5, my: 1 },
                  '& li': { mb: 0.5 },
                  '& p': { my: 1 },
                  '& dl': { my: 1 },
                  '& dt': { fontWeight: 700, color: appColors.text.heading, mt: 1.5 },
                  '& dd': { ml: 2, mb: 1, color: appColors.text.body },
                }}
              >
                {section.body}
              </Typography>
            </Box>
          ))}

          {/* Related topics */}
          {topic.relatedTopicSlugs && topic.relatedTopicSlugs.length > 0 && (
            <HelpRelatedTopics slugs={topic.relatedTopicSlugs} />
          )}

          {/* Back link */}
          <Box sx={{ mt: 4 }}>
            <Button variant="text" startIcon={<BackIcon />} onClick={() => navigate('/help')}>
              Back to Help Center
            </Button>
          </Box>
        </Box>

        {/* On this page nav — desktop sidebar */}
        <HelpOnThisPageNav sections={topic.sections} />
      </Box>
    </Box>
  );
};

export default HelpTopicPage;
